import React, { useEffect, useState } from "react";

import { decodeTiffBuffer } from "../helpers/tiffDecode";
import { fetchTiffArrayBuffer } from "../helpers/tiffHelper";

export interface TiffViewerProps {
  /** `blob:` / `http(s):` URL, `data:`, or raw base64 TIFF bytes. */
  source: string;
  fileName?: string;
}

const TiffViewer: React.FC<TiffViewerProps> = ({ source, fileName }) => {
  const [pageSrcs, setPageSrcs] = useState<string[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setPageSrcs([]);
    setPageIndex(0);

    if (!source?.trim()) {
      setBusy(false);
      return;
    }

    setBusy(true);
    (async () => {
      try {
        const buffer = await fetchTiffArrayBuffer(source);
        const urls = decodeTiffBuffer(buffer);
        if (!cancelled) {
          setPageSrcs(urls);
          setPageIndex(0);
        }
      } catch (e) {
        if (!cancelled) {
          setPageSrcs([]);
          setError(
            e instanceof Error
              ? e.message
              : "Could not display this TIFF file.",
          );
        }
      } finally {
        if (!cancelled) {
          setBusy(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [source]);

  if (!source?.trim()) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center overflow-auto p-6 text-sm text-hex-gray-500 bg-white border-t border-hex-gray-100">
        No TIFF to display.
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center overflow-auto p-6 text-center text-md text-hex-gray-600 border-t border-hex-gray-100">
        {error}
      </div>
    );
  }

  if (busy || pageSrcs.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center overflow-hidden text-hex-gray-500 bg-white border-t border-hex-gray-100">
        <span className="text-sm">
          {busy ? "Loading TIFF…" : "No preview."}
        </span>
      </div>
    );
  }

  const currentSrc = pageSrcs[pageIndex] ?? pageSrcs[0];
  const total = pageSrcs.length;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-t border-hex-gray-100">
      {total > 1 && (
        <div className="flex shrink-0 items-center justify-center gap-2 border-b border-hex-gray-200 bg-white px-3 py-2">
          <button
            type="button"
            className="rounded border border-hex-gray-300 bg-white px-2 py-1 text-sm text-chat-dark enabled:hover:bg-hex-gray-50 disabled:opacity-40"
            disabled={pageIndex <= 0}
            onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
            aria-label="Previous page"
          >
            Previous
          </button>
          <span className="text-sm text-hex-gray-600">
            Page {pageIndex + 1} of {total}
          </span>
          <button
            type="button"
            className="rounded border border-hex-gray-300 bg-white px-2 py-1 text-sm text-chat-dark enabled:hover:bg-hex-gray-50 disabled:opacity-40"
            disabled={pageIndex >= total - 1}
            onClick={() => setPageIndex((i) => Math.min(total - 1, i + 1))}
            aria-label="Next page"
          >
            Next
          </button>
        </div>
      )}
      
      <div className="min-h-0 min-w-0 flex-1 overflow-auto p-3">
        <div className="flex min-h-full min-w-0 items-center justify-center">
          <img
            src={currentSrc}
            alt={
              fileName
                ? `TIFF preview: ${fileName}${total > 1 ? ` (page ${pageIndex + 1})` : ""}`
                : "TIFF preview"
            }
            className="max-h-full max-w-full object-contain shadow-xs rounded"
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
          />
        </div>
      </div>
    </div>
  );
};

export default TiffViewer;
